import { useCallback, useEffect, useImperativeHandle, forwardRef, useState, useRef } from 'react';
import { Box, Text } from '@chakra-ui/react';
import { loadEnterpriseRecaptcha, executeEnterpriseRecaptcha, logger } from '@kuow/core';

export interface RecaptchaProps {
  action?: string;
  onVerify: (token: string) => void;
  onError?: (error: Error) => void;
  onLoad?: () => void;
  showStatus?: boolean;
  statusMessages?: {
    idle?: string;
    loading?: string;
    verified?: string;
    error?: string;
  };
}

export interface RecaptchaHandle {
  execute: () => Promise<string>;
  reset: () => Promise<string>;
}

const DEFAULT_STATUS_MESSAGES = {
  // Required reCAPTCHA branding text when the badge is hidden.
  // See https://developers.google.com/recaptcha/docs/faq
  idle: 'This site is protected by reCAPTCHA.',
  loading: 'Verifying reCAPTCHA…',
  verified: 'This site is protected by reCAPTCHA.',
  error: 'Unable to verify reCAPTCHA. Please try again.',
};

type RecaptchaStatus = 'idle' | 'loading' | 'verified' | 'error';

/**
 * Recaptcha component for Google reCAPTCHA Enterprise
 * 
 * Implements invisible reCAPTCHA Enterprise with automatic execution and status display.
 * The reCAPTCHA script is loaded on mount and can be executed programmatically.
 */
export const Recaptcha = forwardRef<RecaptchaHandle, RecaptchaProps>(
  ({ action = 'submit', onVerify, onError, onLoad, showStatus = true, statusMessages = {} }, ref) => {
    const [status, setStatus] = useState<RecaptchaStatus>('idle');
    const onLoadCalledRef = useRef(false);
    const executingRef = useRef<Promise<string> | null>(null);
    const mountedRef = useRef(false);

    useEffect(() => {
      mountedRef.current = true;
      
      // Pre-load the reCAPTCHA library on mount
      loadEnterpriseRecaptcha().catch((error: unknown) => {
        logger.error('Failed to load reCAPTCHA Enterprise', { error });
      });
      
      return () => {
        mountedRef.current = false;
      };
    }, []);

    const notifyVerification = useCallback(
      (token: string) => {
        onVerify(token);
      },
      [onVerify]
    );

    const execute = useCallback(async (): Promise<string> => {
      if (executingRef.current) {
        return executingRef.current;
      }

      const execution = (async () => {
        try {
          if (mountedRef.current) {
            setStatus('loading');
          }

          await loadEnterpriseRecaptcha();

          if (mountedRef.current && !onLoadCalledRef.current && onLoad) {
            onLoadCalledRef.current = true;
            onLoad();
          }

          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
          const token = await executeEnterpriseRecaptcha(action);

          if (mountedRef.current) {
            setStatus('verified');
            notifyVerification(token);
          }

          return token;
        } catch (error) {
          const err = error instanceof Error ? error : new Error('reCAPTCHA execution failed');
          
          if (mountedRef.current) {
            setStatus('error');
            notifyVerification('');

            if (onError) {
              onError(err);
            }

            logger.error('reCAPTCHA Enterprise execution failed', { error: err });
          }

          throw err;
        } finally {
          executingRef.current = null;
        }
      })();

      executingRef.current = execution;
      return execution;
    }, [action, notifyVerification, onError, onLoad]);

    useImperativeHandle(ref, () => ({
      reset: () => {
        return execute().catch(() => '');
      },
      execute,
    }));

    const resolvedStatusMessages = { ...DEFAULT_STATUS_MESSAGES, ...statusMessages };
    const statusMessage = resolvedStatusMessages[status];

    return (
      <Box
        data-recaptcha-status={status}
        role="status"
        aria-live="polite"
      >
        {showStatus && statusMessage && (
          <Text 
            fontSize="xs" 
            color="fg.muted" 
            textAlign="start"
            data-recaptcha-status-message={status}
          >
            {statusMessage}
          </Text>
        )}
      </Box>
    );
  }
);

Recaptcha.displayName = 'Recaptcha';

